import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  repeat,
  switchMap,
  tap,
  timer,
} from 'rxjs';

import {
  AccountsTableStaleness,
  ApplyWorkflowNodePayload,
  runtimeActions,
  runtimeSelectors,
  WorkflowStepDebugger,
} from '@blaze-runtime/engine-data-access';
import { RuntimeErrorMessage } from '@blaze-runtime/engine-domain';
import {
  AggregateActionService,
  ApiCallActionService,
  ComputeActionService,
  CopyToClipboardActionService,
  DatasetDeleteActionService,
  DatasetGetActionService,
  DatasetInsertActionService,
  DatasetMergeActionService,
  DatasetUpdateActionService,
  DialogActionService,
  DownloadActionService,
  ExecuteJavascriptActionService,
  ExpandActionService,
  FilterActionService,
  JoinActionService,
  LookupActionService,
  NavigateNodeExecutionService,
  NodeExecutionResultService,
  ReadCsvActionService,
  SaveFileActionService,
  StoreVariableActionService,
  throwInvalidNodeError,
  UserLocationActionService,
} from '@blaze-runtime/workflow-feature';
import { VariableInfo } from '@blaze-shared/api-contracts-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { WorkflowLoggerService } from '@blaze-shared/platform-data-access';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import {
  convertBlazeSchemaToWorkflowSchema,
  isBlazeTableSchema,
  isDatasetSchema,
} from '@blaze-shared/schema-domain';
import { isNullish } from '@blaze-shared/shared-util';
import { VariablesFacade } from '@blaze-shared/variables-data-access';
import { normalizeVarName } from '@blaze-shared/variables-util';
import {
  AggregateWorkflowNode,
  ApiCallWorkflowNode,
  CloseDialogWorkflowNode,
  ComputeWorkflowNode,
  ConfirmDialogWorkflowNode,
  CopyToClipboardWorkflowNode,
  DatasetWorkflowNodeBase,
  DelayWorkflowNode,
  DeleteWorkflowNode,
  DownloadWorkflowNode,
  ExceptWorkflowNode,
  ExecuteJavascriptWorkflowNode,
  ExecuteWorkflowNode,
  ExpandWorkflowNode,
  FilterWorkflowNode,
  GetUserLocationWorkflowNode,
  GetWorkflowNode,
  getWorkflowNodeById,
  InsertWorkflowNode,
  isApiCallNode,
  isBackendOnlyNode,
  isDownloadNode,
  isFilterNode,
  isGetNode,
  isGetUserLocationNode,
  isNavigateNode,
  isServerOnlyWorkflow,
  JoinWorkflowNode,
  LookupWorkflowNode,
  MergeWorkflowNode,
  OpenDialogWorkflowNode,
  ReadCsvDataWorkflowNode,
  SaveFileWorkflowNode,
  StoreVariableWorkflowNode,
  TerminateWorkflowNode,
  UpdateWorkflowNode,
} from '@blaze-shared/workflow-domain';

import { ApplicationDialogService } from '../../services/dialog/application-dialog.service';
import { RuntimeService } from '../../services/runtime.service';

import { NodePromptOutput, WorkflowEffectsService } from './workflow-effects.service';

@Injectable()
export class WorkflowNodesEffects {
  private readonly accountsTableStaleness = inject(AccountsTableStaleness);
  private readonly actions$ = inject(Actions);
  private readonly aggregateActionService = inject(AggregateActionService);
  private readonly apiCallActionService = inject(ApiCallActionService);
  private readonly computeActionService = inject(ComputeActionService);
  private readonly copyToClipboardActionService = inject(CopyToClipboardActionService);
  private readonly datasetDeleteActionService = inject(DatasetDeleteActionService);
  private readonly datasetGetActionService = inject(DatasetGetActionService);
  private readonly datasetInsertActionService = inject(DatasetInsertActionService);
  private readonly datasetMergeActionService = inject(DatasetMergeActionService);
  private readonly datasetUpdateActionService = inject(DatasetUpdateActionService);
  private readonly dialogActionService = inject(DialogActionService);
  private readonly downloadActionService = inject(DownloadActionService);
  private readonly executeJavascriptActionService = inject(ExecuteJavascriptActionService);
  private readonly expandActionService = inject(ExpandActionService);
  private readonly filterActionService = inject(FilterActionService);
  private readonly joinActionService = inject(JoinActionService);
  private readonly lookupActionService = inject(LookupActionService);
  private readonly readCsvActionService = inject(ReadCsvActionService);
  private readonly saveFileActionService = inject(SaveFileActionService);
  private readonly storeVariableActionService = inject(StoreVariableActionService);
  private readonly userLocationActionService = inject(UserLocationActionService);
  private readonly applicationDialogService = inject(ApplicationDialogService);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly injector = inject(Injector);
  private readonly iss = inject(IntelliSenseService);
  private readonly nodeExecutionResultService = inject(NodeExecutionResultService);
  private readonly runtimeService = inject(RuntimeService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store<any>);
  private readonly variablesFacade = inject(VariablesFacade);
  private readonly workflowEffectsService = inject(WorkflowEffectsService);
  private readonly workflowLogger = inject(WorkflowLoggerService);
  private readonly workflowStepDebugger = inject(WorkflowStepDebugger);

  /**
   * Apply workflow node of type: aggregate.
   */
  applyWorkflowNodeAggregate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Aggregate'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as AggregateWorkflowNode;
            if (
              !node.functions?.length &&
              !node.functionDefs?.length &&
              !node.groupByColumns?.length
            ) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          map((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;

            const node = workflowNode as AggregateWorkflowNode;

            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.functions}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            const actionOutputData = this.aggregateActionService.aggregate(node, nodeInput);
            schema = this.iss.getSchemaForAggregate(schema, node);
            this.workflowLogger.log(`Output "${node.nodeType}" (${node.functions}}):`, {
              extra: {
                actionOutputData,
                schema,
              },
              workflowExecutionId,
            });
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);

            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              nodeInput: actionOutputData,
              prevNodeId: workflowNode?.id,
              schema,
              workflowNode: nextNode,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Aggregate',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: apicall.
   */
  applyWorkflowNodeApiCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'ApiCall'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as ApiCallWorkflowNode;
            if (!node.connectorId) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          concatLatestFrom(() => this.store.select(runtimeSelectors.selectProjectId)),
          mergeMap(([nodePayload, projectId]) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;

            if (!workflow) {
              throw new Error('workflow missing'); /* sanity check */
            }
            const node = workflowNode as ApiCallWorkflowNode;

            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.actionName}):`, {
              extra: {
                workflowNode,
                nodeInput,
              },
              workflowExecutionId,
            });

            const originalNodeInput = nodeInput;
            return this.workflowEffectsService
              .showNodePromptOrContinue(workflow, node, ctx, nodeInput, schema)
              .pipe(
                mergeMap((promptOutput: NodePromptOutput) => {
                  if (this.workflowEffectsService.wasCancelledByDialog(promptOutput)) {
                    return of(
                      this.workflowEffectsService.cancelNextWorkflowNode(
                        nodePayload,
                        originalNodeInput,
                        workflowNode?.id,
                      ),
                    );
                  } else if (this.workflowEffectsService.canWorkflowBeResumed(nodePayload)) {
                    const userInputs = this.workflowEffectsService.formatUserInput(
                      promptOutput?.dialogOutputData,
                      schema,
                    );
                    return [
                      this.workflowEffectsService.applyNextWorkflowNode({
                        ...nodePayload,
                        userInputs: userInputs,
                      }),
                    ];
                  }
                  return this.apiCallActionService
                    .apicall(node, promptOutput.nodeInput, ctx, schema, projectId, workflow.id)
                    .pipe(
                      map((actionOutputData) => {
                        schema =
                          this.iss.getSchemaForApiCall(schema, node) ??
                          this.iss.getWorkflowSchemaFromData(
                            schema,
                            actionOutputData,
                            node.actionName,
                            workflow?.id,
                          );
                        const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                        this.workflowEffectsService.validateNextNode(node, nextNode);

                        this.workflowLogger.log(`Output "${node.nodeType}" (${node.actionName}):`, {
                          extra: {
                            actionOutputData,
                            nextNode,
                            schema,
                          },
                          workflowExecutionId,
                        });
                        const nextNodePayload: ApplyWorkflowNodePayload = {
                          ...nodePayload,
                          nodeInput: actionOutputData,
                          prevNodeId: workflowNode?.id,
                          schema,
                          workflowNode: nextNode,
                        };
                        return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
                      }),
                    );
                }),
              );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'ApiCall',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: copy to clipboard.
   */
  applyWorkflowNodeCopyToClipboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'CopyToClipboard'),
      mergeMap((action) =>
        of(action).pipe(
          map((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            const node = workflowNode as CopyToClipboardWorkflowNode;

            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.condition}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            const actionOutputData = this.copyToClipboardActionService.copyToClipboard(
              node,
              nodeInput,
              ctx,
            );
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);
            this.workflowLogger.log(`Output "${node.nodeType}":`, {
              extra: {
                actionOutputData,
              },
            });
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              prevNodeId: workflowNode?.id,
              workflowNode: nextNode,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'CopyToClipboard',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: compute.
   */
  applyWorkflowNodeCompute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Compute'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as ComputeWorkflowNode;
            if (!node.name || (!node.value && !node.formula)) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          map((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as ComputeWorkflowNode;
            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.value}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            const actionOutputData = this.computeActionService.compute(
              node,
              nodeInput,
              ctx,
              schema,
            );
            schema = this.iss.getSchemaForCompute(schema, node, actionOutputData);

            this.workflowLogger.log(`Output "${node.nodeType}" (${node.value}):`, {
              extra: {
                actionOutputData,
                schema,
              },
              workflowExecutionId,
            });
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              nodeInput: actionOutputData,
              prevNodeId: workflowNode?.id,
              schema,
              workflowNode: nextNode,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Compute',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: ConfirmDialog (Show Message).
   */
  applyWorkflowNodeConfirmDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'ConfirmDialog'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as ConfirmDialogWorkflowNode;
            if (isNullish(node.title) || isNullish(node.message)) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          concatMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as ConfirmDialogWorkflowNode;
            this.workflowLogger.log(`Apply "${node.nodeType}":`, {
              extra: { node, nodeInput, ctx },
              workflowExecutionId,
            });
            return this.dialogActionService.confirmDialog(node, nodeInput, ctx).pipe(
              map((result) => {
                // Stop execution setting should only be taken into account if there is not Cancel button.
                const stopExecution =
                  !node.buttons.find((btn) => btn.action === 'cancel') && node.stopExecution;
                const isCanceled = stopExecution || !result || result === 'cancel';
                const nextNode = isCanceled
                  ? null
                  : getWorkflowNodeById(node?.nextNodeId, workflow);
                this.workflowEffectsService.validateNextNode(node, nextNode);
                if (!isCanceled) {
                  schema = this.iss.getWorkflowSchemaFromData(schema, nodeInput);
                } else if (isCanceled && workflow) {
                  schema = this.iss.calculateSchemaForWorkflow(workflow).schema ?? schema;
                }
                const nextNodePayload: ApplyWorkflowNodePayload = {
                  ...nodePayload,
                  isWorkflowCancelled: isCanceled,
                  nodeInput: isNullish(nodeInput) ? [{}] : nodeInput, // BL-6166: allow nodes after show message to execute
                  prevNodeId: workflowNode?.id,
                  schema,
                  workflowNode: nextNode,
                };
                return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
              }),
              catchError((error: any) => {
                const actions: Action[] = [];
                if (
                  action.target?.id &&
                  !this.runtimeService.isElementInitialized(action.target.id)
                ) {
                  actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
                }
                actions.push(
                  ...this.workflowEffectsService.getRuntimeErrorActions(
                    error,
                    action.event,
                    action.workflow?.id,
                    'Compute',
                    action.workflowNode?.id,
                  ),
                );
                return actions;
              }),
            );
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: delay.
   */
  applyWorkflowNodeDelay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Delay'),
      mergeMap((action) =>
        of(action).pipe(
          concatMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            const node = workflowNode as DelayWorkflowNode;

            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.delayMs} ms):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            // Wait for the specified amount of time before moving to the next node
            return timer(node.delayMs).pipe(
              map(() => {
                const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                this.workflowEffectsService.validateNextNode(node, nextNode);

                this.workflowLogger.log(
                  `Output "${node.nodeType}" (${node.delayMs} ms): delay completed`,
                  {
                    workflowExecutionId,
                  },
                );
                const nextNodePayload: ApplyWorkflowNodePayload = {
                  ...nodePayload,
                  prevNodeId: workflowNode?.id,
                  workflowNode: nextNode,
                };
                return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
              }),
            );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Delay',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: dataset delete.
   */
  applyWorkflowNodeDatasetDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Delete'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as DatasetWorkflowNodeBase;
            if (!node?.datasetId) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          mergeMap((nodePayload) => {
            const { ctx, event, nodeInput, workflow, workflowExecutionId, workflowNode } =
              nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as DeleteWorkflowNode;
            const tableName = this.schemaService.getTableName(node.datasetId) || node.datasetId;

            this.workflowLogger.log(`Apply "${node.nodeType}":`, {
              extra: {
                node,
                ctx,
              },
              workflowExecutionId,
            });

            let confirmationResult$: Observable<'continue' | 'cancel'> = of('continue');

            const confirm = isServerOnlyWorkflow(workflow) ? false : node.askForConfirmation;
            if (confirm) {
              const contextData = this.workflowEffectsService.getContextData(workflow, nodeInput);
              const context = this.iss.updateRuntimeContext(ctx, contextData);
              const resolvedConfirmationMessage =
                this.schemaService.replaceTagIdsWithValuesWhenString(node.confirmationMessage);
              const confirmationMessage = node.confirmationMessage
                ? this.iss.eval(resolvedConfirmationMessage, context)
                : `Do you want to delete from table ${tableName}?`;

              confirmationResult$ = this.applicationDialogService.openConfirmDialog(
                'Delete confirmation',
                confirmationMessage,
                [
                  {
                    label: 'Delete',
                    action: 'continue',
                    id: 'continue',
                  },
                  {
                    label: 'Cancel',
                    action: 'cancel',
                    id: 'cancel',
                  },
                ],
              );
            }

            return confirmationResult$.pipe(
              switchMap((result) => {
                if (result !== 'continue') {
                  return of(
                    runtimeActions.connectionStepComplete({ event, workflow, workflowExecutionId }),
                  );
                }
                if (this.workflowEffectsService.canWorkflowBeResumed(nodePayload)) {
                  return [this.workflowEffectsService.applyNextWorkflowNode(nodePayload)];
                }
                return this.datasetDeleteActionService
                  .datasetDelete(node, nodeInput, ctx, workflow?.id, workflowExecutionId)
                  .pipe(
                    mergeMap((actionOutputData) => {
                      schema = this.iss.getSchemaForDataset(schema, node);
                      if (
                        (isDatasetSchema(schema) || isBlazeTableSchema(schema)) &&
                        !!workflow?.id
                      ) {
                        schema = convertBlazeSchemaToWorkflowSchema(schema, workflow.id);
                      }

                      this.workflowLogger.log(`Output "${node.nodeType}":`, {
                        extra: {
                          actionOutputData,
                          schema,
                        },
                        workflowExecutionId,
                      });
                      const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                      this.workflowEffectsService.validateNextNode(node, nextNode);
                      const nextNodePayload: ApplyWorkflowNodePayload = {
                        ...nodePayload,
                        nodeInput: actionOutputData,
                        prevNodeId: workflowNode?.id,
                        schema,
                        workflowNode: nextNode,
                      };
                      return [this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload)];
                    }),
                  );
              }),
            );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Delete',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: dataset get.
   */
  applyWorkflowNodeDatasetGet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Get'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as GetWorkflowNode;
            if (!node?.datasetId) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as GetWorkflowNode;

            this.workflowLogger.log(`Apply "get ${node?.datasetId}":`, {
              extra: { node },
              workflowExecutionId,
            });

            return this.datasetGetActionService
              .datasetGet(node, nodeInput, ctx, workflow?.id, workflowExecutionId)
              .pipe(
                mergeMap(({ data }) => {
                  schema = this.iss.getSchemaForDataset(schema, node);
                  if ((isDatasetSchema(schema) || isBlazeTableSchema(schema)) && !!workflow?.id) {
                    schema = convertBlazeSchemaToWorkflowSchema(schema, workflow.id);
                  }

                  this.workflowLogger.log(`Output "${node.nodeType}":`, {
                    extra: { data, schema },
                    workflowExecutionId,
                  });
                  const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                  this.workflowEffectsService.validateNextNode(node, nextNode);
                  const nextActions: Action[] = [];

                  const nextNodePayload: ApplyWorkflowNodePayload = {
                    ...nodePayload,
                    nodeInput: data,
                    prevNodeId: workflowNode?.id,
                    schema,
                    workflowNode: nextNode,
                  };
                  nextActions.push(
                    this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload),
                  );
                  return nextActions;
                }),
              );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Get',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: dataset insert.
   */
  applyWorkflowNodeDatasetInsert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Insert'),
      mergeMap((action) =>
        of(action).pipe(
          filter(({ workflowNode, workflow }) => {
            const node = workflowNode as InsertWorkflowNode;
            if (!node?.datasetId) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
            return !!workflow && node?.nodeType === 'Insert' && !!node.datasetId;
          }),
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            if (!workflow) {
              throw new Error('workflow missing'); /* sanity check */
            }
            const node = workflowNode as InsertWorkflowNode;
            this.workflowLogger.log(`Apply "${node.nodeType}":`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            const originalNodeInput = nodeInput;
            return this.workflowEffectsService
              .showNodePromptOrContinue(workflow, node, ctx, nodeInput, schema)
              .pipe(
                mergeMap((promptOutput) => {
                  if (this.workflowEffectsService.wasCancelledByDialog(promptOutput)) {
                    return of(
                      this.workflowEffectsService.cancelNextWorkflowNode(
                        nodePayload,
                        originalNodeInput,
                        workflowNode?.id,
                      ),
                    );
                  } else if (this.workflowEffectsService.canWorkflowBeResumed(nodePayload)) {
                    const userInputs = this.workflowEffectsService.formatUserInput(
                      promptOutput?.dialogOutputData,
                      schema,
                      true,
                    );
                    return [
                      this.workflowEffectsService.applyNextWorkflowNode({
                        ...nodePayload,
                        userInputs: userInputs,
                      }),
                    ];
                  }
                  return this.datasetInsertActionService
                    .datasetInsert(
                      node,
                      promptOutput.nodeInput,
                      ctx,
                      workflow.id,
                      workflowExecutionId,
                    )
                    .pipe(
                      mergeMap((actionOutputData) => {
                        if (actionOutputData) {
                          schema = this.iss.getSchemaForDataset(schema, node);
                          if (
                            (isDatasetSchema(schema) || isBlazeTableSchema(schema)) &&
                            !!workflow?.id
                          ) {
                            schema = convertBlazeSchemaToWorkflowSchema(schema, workflow.id);
                          }
                        }

                        this.workflowLogger.log(`Output "${node.nodeType}":`, {
                          extra: {
                            actionOutputData,
                            schema,
                          },
                          workflowExecutionId,
                        });
                        const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                        this.workflowEffectsService.validateNextNode(node, nextNode);
                        const nextNodePayload: ApplyWorkflowNodePayload = {
                          ...nodePayload,
                          nodeInput: actionOutputData,
                          prevNodeId: workflowNode?.id,
                          schema,
                          workflowNode: nextNode,
                        };
                        return [this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload)];
                      }),
                    );
                }),
              );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Insert',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: dataset merge.
   */
  applyWorkflowNodeDatasetMerge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Merge'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as MergeWorkflowNode;
            if (!node?.datasetId) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          filter(({ workflowNode, workflow }) => {
            const node = workflowNode as MergeWorkflowNode;
            return !!workflow && node?.nodeType === 'Merge' && !!node.datasetId;
          }),
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, schema, workflow, workflowNode, workflowExecutionId } =
              nodePayload;
            const node = workflowNode as MergeWorkflowNode;
            this.workflowLogger.log(`Apply "${node.nodeType}":`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            return this.datasetMergeActionService
              .datasetMerge(node, nodeInput, ctx, workflow?.id, workflowExecutionId)
              .pipe(
                mergeMap((actionOutputData) => {
                  this.workflowLogger.log(`Output "${node.nodeType}":`, {
                    extra: {
                      actionOutputData,
                      schema,
                    },
                    workflowExecutionId,
                  });
                  const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                  this.workflowEffectsService.validateNextNode(node, nextNode);
                  const nextNodePayload: ApplyWorkflowNodePayload = {
                    ...nodePayload,
                    nodeInput: actionOutputData,
                    prevNodeId: workflowNode?.id,
                    workflowNode: nextNode,
                  };
                  return [this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload)];
                }),
              );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Insert',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: dataset update.
   */
  applyWorkflowNodeDatasetUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Update'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as UpdateWorkflowNode;
            if (!node?.datasetId || (!node.assignments?.length && !node.bulkEdit)) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            if (!workflow) {
              throw new Error('workflow missing'); /* sanity check */
            }
            const node = workflowNode as UpdateWorkflowNode;
            this.workflowLogger.log(`Apply "${node.nodeType}":`, {
              extra: {
                node,
                ctx,
              },
              workflowExecutionId,
            });
            const originalNodeInput = nodeInput;
            return this.workflowEffectsService
              .showNodePromptOrContinue(workflow, node, ctx, nodeInput, schema)
              .pipe(
                mergeMap((promptOutput) => {
                  if (this.workflowEffectsService.wasCancelledByDialog(promptOutput)) {
                    return of(
                      this.workflowEffectsService.cancelNextWorkflowNode(
                        nodePayload,
                        originalNodeInput,
                        workflowNode?.id,
                      ),
                    );
                  } else if (this.workflowEffectsService.canWorkflowBeResumed(nodePayload)) {
                    const userInputs = this.workflowEffectsService.formatUserInput(
                      promptOutput?.dialogOutputData,
                      schema,
                    );
                    return [
                      this.workflowEffectsService.applyNextWorkflowNode({
                        ...nodePayload,
                        userInputs: userInputs,
                      }),
                    ];
                  }
                  return this.datasetUpdateActionService
                    .datasetUpdate(
                      node,
                      promptOutput.nodeInput,
                      ctx,
                      workflow?.id,
                      workflowExecutionId,
                    )
                    .pipe(
                      mergeMap((actionOutputData) => {
                        schema = this.iss.getSchemaForDataset(schema, node);
                        if (
                          (isDatasetSchema(schema) || isBlazeTableSchema(schema)) &&
                          !!workflow?.id
                        ) {
                          schema = convertBlazeSchemaToWorkflowSchema(schema, workflow.id);
                        }

                        this.workflowLogger.log(`Output "${node.nodeType}":`, {
                          extra: {
                            actionOutputData,
                            schema,
                          },
                          workflowExecutionId,
                        });
                        const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                        this.workflowEffectsService.validateNextNode(node, nextNode);
                        const nextNodePayload: ApplyWorkflowNodePayload = {
                          ...nodePayload,
                          nodeInput: actionOutputData,
                          prevNodeId: workflowNode?.id,
                          schema,
                          workflowNode: nextNode,
                        };
                        return [this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload)];
                      }),
                    );
                }),
              );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Update',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: Download.
   */
  applyWorkflowNodeDownload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => isDownloadNode(workflowNode)),
      mergeMap((action) =>
        of(action).pipe(
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, schema, workflow, workflowNode, workflowExecutionId } =
              nodePayload;
            const node = workflowNode as DownloadWorkflowNode;

            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.url}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
                schema,
              },
              workflowExecutionId,
            });
            this.downloadActionService.download(node, nodeInput, ctx);

            this.workflowLogger.log(`Output "${node.nodeType}":`, {
              extra: { schema },
              workflowExecutionId,
            });
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);
            const nextActions: Action[] = [];
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              nodeInput: nodeInput,
              prevNodeId: workflowNode?.id,
              schema,
              workflowNode: nextNode,
            };
            nextActions.push(this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload));
            return nextActions;
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Get',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: OpenDialog.
   */
  applyWorkflowNodeDialogOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'OpenDialog'),
      mergeMap((action) =>
        of(action).pipe(
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as OpenDialogWorkflowNode;
            const dialogSchema = this.iss.getSchemaForDialog(schema, node, nodeInput, ctx);

            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.dialogId}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
                schema,
              },
              workflowExecutionId,
            });
            return this.dialogActionService
              .dialogOpen({
                assignments: node.assignments,
                ctx,
                dialogId: node.dialogId,
                nodeInput,
                nodeType: node.nodeType,
                passDataAsInput: node.passDataAsInput,
                schema: dialogSchema,
              })
              .pipe(
                map(({ payload }) => {
                  // If dialog output is undefined, user closed the dialog without performing an action. Stop the workflow.
                  const nextNode =
                    payload === undefined ? null : getWorkflowNodeById(node?.nextNodeId, workflow);
                  this.workflowEffectsService.validateNextNode(node, nextNode);
                  let actionOutputData: any;
                  if (payload) {
                    actionOutputData = payload;
                    schema = this.iss.getWorkflowSchemaFromData(schema, payload);
                  } else {
                    actionOutputData = nodeInput;
                  }

                  this.workflowLogger.log(
                    `Output "${node.nodeType}" (Dialog ${node.dialogId} was closed):`,
                    {
                      extra: {
                        payload,
                        schema,
                      },
                    },
                  );
                  const nextNodePayload: ApplyWorkflowNodePayload = {
                    ...nodePayload,
                    nodeInput: actionOutputData,
                    prevNodeId: workflowNode?.id,
                    schema,
                    workflowNode: nextNode,
                  };
                  return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
                }),
              );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'OpenDialog',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: CloseDialog.
   */
  applyWorkflowNodeDialogClose$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'CloseDialog'),
      mergeMap((action) =>
        of(action).pipe(
          map((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            const node = workflowNode as CloseDialogWorkflowNode;

            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.dialogId}}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            this.dialogActionService.dialogClose(node, nodeInput, ctx);
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              prevNodeId: workflowNode?.id,
              workflowNode: nextNode,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'CloseDialog',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: Except.
   */
  applyWorkflowNodeExcept$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Except'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode, workflowExecutionId }) => {
            this.workflowLogger.log(
              `Error: attempting to execute Except node ${workflowNode?.id} outside of server environment.`,
              {
                level: 'error',
                workflowExecutionId,
              },
            );
          }),
          map((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            const node = workflowNode as ExceptWorkflowNode;
            this.workflowLogger.log(`Ignoring execution of "${node.nodeType}":`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              prevNodeId: workflowNode?.id,
              workflowNode: nextNode,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Except',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: ExecuteJavascript.
   */
  applyWorkflowNodeExecuteJavascript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'ExecuteJavascript'),
      mergeMap((action) =>
        of(action).pipe(
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, workflowNode, workflowExecutionId } = nodePayload;
            const node = workflowNode as ExecuteJavascriptWorkflowNode;
            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.value}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            // Returning an Promise here
            return from(
              this.executeJavascriptActionService.executeJavascript(node, nodeInput, ctx),
            ).pipe(map((jsExecuteResult) => ({ nodePayload, jsExecuteResult })));
          }),
          map(({ nodePayload, jsExecuteResult }) => {
            const { schema, workflow, workflowNode, workflowExecutionId } = nodePayload;
            const node = workflowNode as ExecuteJavascriptWorkflowNode;

            this.workflowLogger.log(
              `Output "${node.nodeType}" (${node.value}): javascript executed`,
              {
                extra: {
                  schema,
                  jsExecuteResult,
                },
                workflowExecutionId,
              },
            );
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              prevNodeId: workflowNode?.id,
              workflowNode: nextNode,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'ExecuteJavascript',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: ExecuteWorkflow.
   */
  applyWorkflowNodeExecuteWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'ExecuteWorkflow'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as ExecuteWorkflowNode;
            if (!node.workflowId) {
              this.workflowEffectsService.throwInvalidNodeError(node, 'workflowId');
            }
          }),
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            const node = workflowNode as ExecuteWorkflowNode;

            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.workflowId}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });

            let nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            if (nextNode) {
              this.workflowLogger.log(`Unable to run next node ${nextNode?.nodeType}.`, {
                level: 'error',
                workflowExecutionId,
              });
              this.workflowLogger.log(
                `${node.nodeType} node must be the last node in the workflow.`,
                {
                  level: 'error',
                  workflowExecutionId,
                },
              );
              nextNode = null;
            }
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              prevNodeId: workflowNode?.id,
              workflowNode: nextNode,
            };
            return [
              this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload),
              runtimeActions.executeWorkflow({ workflowId: node.workflowId }),
            ];
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'ExecuteWorkflow',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: Expand.
   */
  applyWorkflowNodeExpand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Expand'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as ExpandWorkflowNode;
            if (!node.column) {
              this.workflowEffectsService.throwInvalidNodeError(node, 'columnName');
            }
          }),
          map((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as ExpandWorkflowNode;
            const columnName = this.schemaService.getColumnName(node.column) ?? '';
            this.workflowLogger.log(`Apply "${node.nodeType}" (${columnName}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            const actionOutputData = this.expandActionService.expand(node, nodeInput);
            schema = this.iss.getSchemaForExpand(schema, node, actionOutputData);
            this.workflowLogger.log(`Output "${node.nodeType}" (${columnName}):`, {
              extra: {
                actionOutputData,
                schema,
              },
              workflowExecutionId,
            });
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              nodeInput: actionOutputData,
              prevNodeId: workflowNode?.id,
              schema,
              workflowNode: nextNode,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Expand',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: filter.
   */
  applyWorkflowNodeFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => isFilterNode(workflowNode)),
      mergeMap((action) =>
        of(action).pipe(
          map((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as FilterWorkflowNode;

            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.condition}):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            const actionOutputData = this.filterActionService.filter(node, nodeInput, ctx);
            // Filter outputs data but we are not aware of any schema. In this case, attempt to deduce schema from the
            // action output data.
            if (
              actionOutputData &&
              !schema.columns?.length &&
              Object.keys(actionOutputData)?.length
            ) {
              schema = this.iss.getWorkflowSchemaFromData(schema, actionOutputData);
            }

            this.workflowLogger.log(`Output "${node.nodeType}" (${node.condition}):`, {
              extra: {
                actionOutputData,
                schema,
              },
              workflowExecutionId,
            });
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              nodeInput: actionOutputData,
              prevNodeId: workflowNode?.id,
              schema,
              workflowNode: nextNode,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Filter',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: Location.
   */
  applyWorkflowNodeGetUserLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => isGetUserLocationNode(workflowNode)),
      mergeMap((action) =>
        of(action).pipe(
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as GetUserLocationWorkflowNode;
            this.workflowLogger.log(`Apply "${node.nodeType}"):`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });
            return this.userLocationActionService.getUserLocation().pipe(
              map((actionOutputData) => {
                schema = this.iss.getSchemaForGetUserLocation(schema, node);
                this.workflowLogger.log(`Output "${node.nodeType}"):`, {
                  extra: {
                    actionOutputData,
                    schema,
                  },
                  workflowExecutionId,
                });
                const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                this.workflowEffectsService.validateNextNode(node, nextNode);
                const nextNodePayload: ApplyWorkflowNodePayload = {
                  ...nodePayload,
                  nodeInput: actionOutputData,
                  prevNodeId: workflowNode?.id,
                  schema,
                  workflowNode: nextNode,
                };
                return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
              }),
            );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'GetUserLocation',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: join.
   */
  applyWorkflowNodeJoin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Join'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as JoinWorkflowNode;
            if (!node.condition || !node.targetId) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          concatLatestFrom(() => this.store.select(runtimeSelectors.selectProjectId)),
          mergeMap(([nodePayload, projectId]) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as JoinWorkflowNode;
            this.workflowLogger.log(`Apply "${node.nodeType}" (${node.condition}):`, {
              extra: {
                workflowNode,
              },
              workflowExecutionId,
            });
            const input2Node = getWorkflowNodeById(node?.targetId, workflow);
            if (input2Node?.nextNodeId) {
              this.workflowLogger.log(
                `Join right branch has nodes after the target that are not applied at runtime (only Get/ApiCall data is used)`,
                {
                  extra: {
                    joinNodeId: node.id,
                    targetId: node.targetId,
                    nextNodeId: input2Node.nextNodeId,
                  },
                  level: 'warning',
                  workflowExecutionId,
                },
              );
            }
            let getData;
            if (isGetNode(input2Node)) {
              if (!input2Node.datasetId) {
                this.workflowEffectsService.throwInvalidNodeError(node);
              }
              getData = this.datasetGetActionService.datasetGet(
                input2Node,
                nodeInput,
                ctx,
                workflow?.id,
                workflowExecutionId,
              );
            } else if (isApiCallNode(input2Node)) {
              getData = this.apiCallActionService.apicall(
                input2Node,
                nodeInput,
                ctx,
                schema,
                projectId,
                workflow?.id || '',
              );
            } else {
              this.workflowLogger.log(`Invalid target node for join action`, {
                extra: {
                  input2Node,
                },
                level: 'error',
                workflowExecutionId,
              });
              this.workflowEffectsService.throwInvalidNodeError(input2Node);
              return of();
            }
            return getData.pipe(
              mergeMap((input2NodeData) => {
                input2NodeData = input2NodeData.data;
                const datasetNode = isGetNode(input2Node) ? input2Node : null;
                const input2TableName = datasetNode
                  ? this.runtimeService.getDatasetName(datasetNode)
                  : '';

                this.workflowLogger.log(`Inputs "${node.nodeType}" (${node.condition}):`, {
                  extra: { nodeInput, input2NodeData },
                  workflowExecutionId,
                });
                const actionOutputData = this.joinActionService.join(
                  node,
                  nodeInput,
                  input2NodeData,
                  ctx,
                  input2TableName,
                );
                const input2Schema = datasetNode
                  ? this.schemaService.getSchema(datasetNode.datasetId as string)
                  : this.iss.getWorkflowSchemaFromData(
                      schema,
                      input2NodeData,
                      (input2Node as ApiCallWorkflowNode).actionName,
                      workflow?.id,
                    );

                this.workflowLogger.log(`Inputs "${node.nodeType}" schema1:`, {
                  extra: {
                    schema,
                  },
                  workflowExecutionId,
                });
                this.workflowLogger.log(`Inputs "${node.nodeType}" schema2:`, {
                  extra: { input2Schema },
                  workflowExecutionId,
                });
                if (!schema || !input2Schema) {
                  this.workflowLogger.log(`Missing schema required for join. Schema1:`, {
                    extra: {
                      schema,
                      input2Schema,
                    },
                    workflowExecutionId,
                    level: 'error',
                  });
                  throw new Error(RuntimeErrorMessage.MissingSchemaInfo);
                }

                schema = this.iss.getSchemaForJoin(schema, input2Schema, node);
                const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                this.workflowEffectsService.validateNextNode(node, nextNode);

                this.workflowLogger.log(`Output "join" (${node.condition}):`, {
                  extra: {
                    actionOutputData,
                    schema,
                  },
                  workflowExecutionId,
                });

                const nextActions: Action[] = [];
                const nextNodePayload: ApplyWorkflowNodePayload = {
                  ...nodePayload,
                  nodeInput: actionOutputData,
                  prevNodeId: workflowNode?.id,
                  schema,
                  workflowNode: nextNode,
                };
                nextActions.push(
                  this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload),
                );
                return nextActions;
              }),
            );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Join',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: apicall.
   */
  applyWorkflowNodeLookup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Lookup'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as LookupWorkflowNode;
            if (!node.datasetId || !node.joinCondition) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as LookupWorkflowNode;
            const isPaginated = this.runtimeService.isPaginated(workflow?.id);

            this.workflowLogger.log(`Apply "${node.nodeType}":`, {
              extra: {
                workflowNode,
                nodeInput,
                isPaginated,
              },
              workflowExecutionId,
            });
            return this.lookupActionService.lookup(node, nodeInput, ctx, schema, 50).pipe(
              map((actionOutputData) => {
                const targetNodeSchema = this.schemaService.getSchema(node.datasetId);
                if (targetNodeSchema) {
                  schema = this.iss.getSchemaForLookup(schema, node);
                } else {
                  schema = this.iss.getWorkflowSchemaFromData(schema, actionOutputData);
                }
                const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                this.workflowEffectsService.validateNextNode(node, nextNode);

                this.workflowLogger.log(`Output "${node.nodeType}":`, {
                  extra: {
                    actionOutputData,
                    nextNode,
                    schema,
                  },
                  workflowExecutionId,
                });

                const nextNodePayload: ApplyWorkflowNodePayload = {
                  ...nodePayload,
                  nodeInput: actionOutputData,
                  prevNodeId: workflowNode?.id,
                  schema,
                  workflowNode: nextNode,
                };
                return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
              }),
            );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Lookup',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: navigate.
   */
  applyWorkflowNodeNavigate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Navigate'),
      mergeMap((nodePayload) =>
        of(nodePayload).pipe(
          mergeMap((nodePayload) => {
            const { ctx, event, nodeInput, schema, workflow, workflowNode } = nodePayload;

            if (!isNavigateNode(workflowNode) || !workflow) {
              // This throws an error
              throwInvalidNodeError(workflowNode);
            }

            return runInInjectionContext(this.injector, () => {
              const nodeExecutionService = new NavigateNodeExecutionService(workflowNode);
              return nodeExecutionService.execute({
                nodeInput,
                schema,
                workflow,
                event,
                runtimeContext: ctx,
              });
            });
          }),
          concatMap((nodeExecutionResult) => {
            const nextNodePayload = this.nodeExecutionResultService.getNextNodePayloadFromResult(
              nodePayload,
              nodeExecutionResult,
            );
            return [this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload)];
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (
              nodePayload.target?.id &&
              !this.runtimeService.isElementInitialized(nodePayload.target.id)
            ) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: nodePayload.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                nodePayload.event,
                nodePayload.workflow?.id,
                'Navigate',
                nodePayload.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Apply workflow node of type: read csv data.
   */
  applyWorkflowNodeReadCsvData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'ReadCsvData'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode, nodeInput }) => {
            const node = workflowNode as ReadCsvDataWorkflowNode;
            if (!node.csvFileUrl) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            } else if (!nodeInput) {
              this.workflowEffectsService.throwInsufficientInputDataError(node, nodeInput);
            } else if (Array.isArray(nodeInput)) {
              this.workflowEffectsService.throwInvalidInputDataError(
                node,
                nodeInput,
                'Input data cannot be an array',
              );
            }
          }),
          mergeMap((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as ReadCsvDataWorkflowNode;
            const getData = this.readCsvActionService.readCsvData(node, nodeInput, ctx) || of([]);
            return getData.pipe(
              mergeMap((readCsvResponse) => {
                let columnOriginNodes = {};
                if (node.schema && 'columnOriginNodes' in node.schema) {
                  columnOriginNodes = node.schema.columnOriginNodes ?? {};
                }
                schema = {
                  columnOriginNodes,
                  columns: node.schema?.columns?.length
                    ? node.schema.columns
                    : readCsvResponse?.columns || [],
                  displayColumn: '',
                  expandsLinks: false,
                  id: schema.workflowId,
                  name: '',
                  tableId: '',
                  tableIds: [],
                  tableName: '',
                  workflowId: schema.workflowId,
                };
                const actionOutputData = readCsvResponse.data || [];
                const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
                this.workflowEffectsService.validateNextNode(node, nextNode);
                const nextNodePayload: ApplyWorkflowNodePayload = {
                  ...nodePayload,
                  nodeInput: actionOutputData,
                  prevNodeId: workflowNode?.id,
                  schema,
                  workflowNode: nextNode,
                };
                return [this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload)];
              }),
            );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'ReadCsvData',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
    ),
  );

  /**
   * Apply workflow node of type: savefile.
   */
  applyWorkflowNodeSaveFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'SaveFile'),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as SaveFileWorkflowNode;
            if (!node.fileType) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          map((nodePayload) => {
            const { ctx, nodeInput, schema, workflow, workflowNode } = nodePayload;
            const node = workflowNode as SaveFileWorkflowNode;
            this.saveFileActionService.saveFile(node, nodeInput, ctx, schema);
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              prevNodeId: workflowNode?.id,
              workflowNode: nextNode,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'SaveFile',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
    ),
  );

  /**
   * Apply workflow node of type: assign or storevariable.
   */
  applyWorkflowNodeStoreVariable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(
        ({ workflowNode }) =>
          workflowNode?.nodeType === 'Assign' || workflowNode?.nodeType === 'StoreVariable',
      ),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            const node = workflowNode as StoreVariableWorkflowNode;
            if (
              !node.scope ||
              isNullish(node.value) ||
              node.value === '' ||
              (!node.name && !node.outputName)
            ) {
              this.workflowEffectsService.throwInvalidNodeError(node);
            }
          }),
          concatLatestFrom(() => this.store.select(runtimeSelectors.selectProjectId)),
          mergeMap(([nodePayload, projectId]) => {
            const { ctx, nodeInput, schema, workflow, workflowNode, workflowExecutionId } =
              nodePayload;
            const node = workflowNode as StoreVariableWorkflowNode;
            const varName = node.name || node.outputName;
            this.workflowLogger.log(`Store ${varName}`, {
              extra: {
                node,
                schema,
              },
              workflowExecutionId,
            });
            const variable = this.storeVariableActionService.storevariable(
              node,
              nodeInput,
              ctx,
              projectId,
            );
            const nextNode = getWorkflowNodeById(node?.nextNodeId, workflow);
            this.workflowEffectsService.validateNextNode(node, nextNode);

            this.workflowLogger.log(
              `Store ${varName} = ${variable.value} in scope: ${variable.scope}`,
              {
                extra: {
                  variable,
                  schema,
                },
                workflowExecutionId,
              },
            );
            // Check whether stored variable already exists
            const storedVariable = this.variablesFacade.getVariableInfo(varName);
            let storeRequest: Observable<VariableInfo | null>;
            if (
              (storedVariable?.scope === 'Local' || storedVariable?.scope === undefined) &&
              variable.scope === 'Local'
            ) {
              // Locally scoped variable - store or overwrite one in local storage but only if it doesn't exist in
              // a different scope.
              this.variablesFacade.setLocalVariable(variable);
              storeRequest = of(variable);
            } else if (variable.scope === 'Workflow') {
              // Keep parsed variable in memory for the time workflow runs.
              storeRequest = of(variable);
            } else if (storedVariable && storedVariable?.scope !== variable.scope) {
              // Attempting to store a variable with the name that already exists in a different scope
              this.workflowEffectsService.throwVariableExistsInDifferentScopeError(storedVariable);
              storeRequest = of(storedVariable);
            } else if (
              storedVariable &&
              storedVariable.scope === variable.scope &&
              variable.value !== undefined
            ) {
              // Variable is already stored on the server. Prepare updated variable and set request to update
              const updatedVariable: VariableInfo = {
                ...storedVariable,
                value: variable.value,
                projectId: variable.projectId,
                scope: variable.scope,
              };
              storeRequest = this.variablesFacade.updateVariableInfo(updatedVariable);
            } else if (!storedVariable) {
              // Variable hasn't yet been stored on the server. Add new variable.
              storeRequest = this.variablesFacade.addVariableInfo(variable);
            } else {
              storeRequest = of(null);
            }
            return storeRequest.pipe(
              map((storedVar) => {
                if (storedVar) {
                  // Update context with the new variable
                  const varKey = normalizeVarName(storedVar.name);
                  ctx.storedVars[varKey] = storedVar.value;
                }
                const nextNodePayload: ApplyWorkflowNodePayload = {
                  ...nodePayload,
                  prevNodeId: workflowNode?.id,
                  workflowNode: nextNode,
                };
                return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
              }),
            );
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'StoreVariable',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
    ),
  );

  /**
   * Apply workflow node of type: Terminate.
   */
  applyWorkflowNodeTerminate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => workflowNode?.nodeType === 'Terminate'),
      mergeMap((action) =>
        of(action).pipe(
          map((nodePayload) => {
            const { ctx, nodeInput, workflow, workflowNode, workflowExecutionId } = nodePayload;
            let { schema } = nodePayload;
            const node = workflowNode as TerminateWorkflowNode;

            this.workflowLogger.log(`Apply "${node.nodeType}"`, {
              extra: {
                node,
                nodeInput,
                ctx,
              },
              workflowExecutionId,
            });

            if (workflow) {
              schema = this.iss.calculateSchemaForWorkflow(workflow).schema ?? schema;
            }

            this.workflowLogger.log(`Output "${node.nodeType}":`, {
              extra: {
                actionOutputData: [],
                schema,
              },
              workflowExecutionId,
            });
            const nextNodePayload: ApplyWorkflowNodePayload = {
              ...nodePayload,
              nodeInput: [],
              prevNodeId: workflowNode?.id,
              schema,
              workflowNode: null,
            };
            return this.workflowEffectsService.applyNextWorkflowNode(nextNodePayload);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                'Terminate',
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Step 3a: Throw error on unsupported node type.
   */
  applyWorkflowNodeUnsupported$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(
        ({ workflowNode }) => !!workflowNode?.nodeType && isBackendOnlyNode(workflowNode?.nodeType),
      ),
      mergeMap((action) =>
        of(action).pipe(
          tap(({ workflowNode }) => {
            this.workflowEffectsService.throwUnsupportedFrontendNode(workflowNode);
          }),
          catchError((error: any) => {
            const actions: Action[] = [];
            if (action.target?.id && !this.runtimeService.isElementInitialized(action.target.id)) {
              actions.push(runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }));
            }
            actions.push(
              ...this.workflowEffectsService.getRuntimeErrorActions(
                error,
                action.event,
                action.workflow?.id,
                action.workflowNode?.nodeType,
                action.workflowNode?.id,
              ),
            );
            return actions;
          }),
        ),
      ),
      repeat(),
    ),
  );

  /**
   * Step 3b: No more workflow nodes need to be applied. Exit the cycle.
   */
  completeWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflowNode),
      filter(({ workflowNode }) => !workflowNode),
      mergeMap((nodePayload) => {
        const { affectedDatasetIds, ctx, event, nodeInput, target, workflow, workflowExecutionId } =
          nodePayload;

        // If a workflow execution affected any datasets, we need to refresh them so that
        // UI elements can properly update.
        if (affectedDatasetIds?.length) {
          this.accountsTableStaleness.markStale(
            affectedDatasetIds.some((datasetId) => this.schemaService.isAccountsTable(datasetId)),
          );
          this.datasetRowsService.notifyDatasetsChanged(affectedDatasetIds);
          this.schemaService.datasetIdsToRefresh$.next(affectedDatasetIds);
        }

        // Adjust schema and output data if workflow output is set to an assigned variable.
        const { outputData, outputSchema } = this.workflowEffectsService.createWorkflowOutputData(
          workflow,
          ctx,
          nodeInput,
          nodePayload.schema,
        );

        this.workflowLogger.log(`Workflow ${workflow?.id} completed. Output:`, {
          extra: { event, nodeInput, outputData, outputSchema },
          workflowExecutionId,
        });

        // Workflow data is what would've normally been input data for the next node.
        const actions: Action[] = [
          runtimeActions.connectionStepComplete({
            event,
            workflow,
            workflowExecutionId,
            outputData,
            workflowSchema: outputSchema,
            updateSource: 'workflow',
          }),
          runtimeActions.formSubmitComplete({ workflow }),
        ];
        if (target?.id && !this.runtimeService.isElementInitialized(target.id)) {
          actions.push(runtimeActions.uiWorkflowExecuted({ elementId: target.id }));
        }
        return actions;
      }),
    ),
  );
}
